import './style.scss';
import {
  addBeneficiary,
  addBeneficiaryToActivityGroup,
  addBeneficiaryToSchool,
  duplicateBeneficiary,
  reactivateBeneficiary,
  searchBeneficiariesByDocument
} from '../../API/apiService';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUserName } from '../../contexts/userName';

function AddBeneficiaryModal({ info, setOpenModalAdd, updateData, backTo }) {
  const { userId } = useUserName();
  const [inputValue, setInputValue] = useState('');
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [resultInfo, setResultInfo] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const navigate = useNavigate();
  const isSocialControlContext = Boolean(info?.activityGroup);
  const isSchoolOnlyContext = Boolean(info?.school) && !info?.group;
  const addButtonLabel = isSchoolOnlyContext
    ? 'AGREGAR A LA ASESORIA'
    : 'AGREGAR AL GRUPO';
  const duplicateButtonLabel = isSchoolOnlyContext
    ? 'DUPLICAR EN LA ASESORIA'
    : 'DUPLICAR EN EL GRUPO';

  function getBeneficiaryId(beneficiary) {
    return Number(beneficiary?.id);
  }

  function getBeneficiaryGroupId(beneficiary) {
    return Number(beneficiary?.group_id ?? beneficiary?.group?.id);
  }

  function getBeneficiarySchoolId(beneficiary) {
    return Number(beneficiary?.school_id ?? beneficiary?.school?.id);
  }

  function getBeneficiaryActivityGroupId(beneficiary) {
    return Number(
      beneficiary?.activity_group_id ??
      beneficiary?.activityGroupId ??
      beneficiary?.activityGroup?.id
    );
  }

  function isSameContext(beneficiary) {
    if (isSocialControlContext) {
      return (
        getBeneficiaryActivityGroupId(beneficiary) ===
        Number(info.activityGroup)
      );
    }
    if (isSchoolOnlyContext) {
      return (
        !getBeneficiaryGroupId(beneficiary) &&
        getBeneficiarySchoolId(beneficiary) === Number(info.school)
      );
    }

    return getBeneficiaryGroupId(beneficiary) === Number(info.group);
  }

  function beneficiaryHasContext(beneficiary) {
    return Boolean(
      getBeneficiaryActivityGroupId(beneficiary) ||
      getBeneficiaryGroupId(beneficiary) ||
      getBeneficiarySchoolId(beneficiary)
    );
  }

  function mergeBlockedBeneficiary(blockedBeneficiary) {
    if (!blockedBeneficiary?.id) return;
    setBeneficiaries(currentList => {
      const exists = currentList.some(item => Number(item?.id) === Number(blockedBeneficiary.id));
      if (exists) {
        return currentList.map(item =>
          Number(item?.id) === Number(blockedBeneficiary.id)
            ? { ...item, ...blockedBeneficiary }
            : item
        );
      }
      return [blockedBeneficiary, ...currentList];
    });
  }

  function finishContextChange() {
    updateData?.(
      isSocialControlContext
        ? info.activityGroup
        : isSchoolOnlyContext
          ? info.school
          : info.group
    );
    setOpenModalAdd(false);
  }

  function handleBlockedResponse(response) {
    if (response?.blocked) {
      mergeBlockedBeneficiary(response.beneficiary);
      return true;
    }
    return false;
  }

  async function addToContext(beneficiary) {
    const response = isSocialControlContext
      ? await addBeneficiaryToActivityGroup(
          info.activityGroup,
          beneficiary.id
        )
      : isSchoolOnlyContext
        ? await addBeneficiaryToSchool(info.school, beneficiary.id)
        : await addBeneficiary(
          info.group,
          beneficiary.id,
          info.school
        );

    if (handleBlockedResponse(response)) {
      return;
    }

    if (response) {
      finishContextChange();
    }
  }

  async function duplicateToContext(beneficiary) {
    if (
      !beneficiary?.id ||
      (!info?.school && !info?.group && !info?.activityGroup)
    ) {
      toast('No se pudo identificar el lugar para duplicar', {
        type: 'warning'
      });
      return;
    }

    setIsDuplicating(true);

    try {
      const duplicated = await duplicateBeneficiary(beneficiary.id, {
        schoolId: info.school,
        groupId: info.group,
        activityGroupId: info.activityGroup,
        userId
      });

      if (handleBlockedResponse(duplicated)) {
        return;
      }

      if (duplicated) {
        toast(
          `Beneficiario duplicado con documento ${duplicated.document || ''}`,
          {
            type: 'success'
          }
        );
        finishContextChange();
      }
    } finally {
      setIsDuplicating(false);
    }
  }

  async function reactivateInContext(beneficiary) {
    const beneficiaryId = getBeneficiaryId(beneficiary);
    if (!beneficiaryId) return;

    setReactivatingId(beneficiaryId);
    try {
      const reactivated = await reactivateBeneficiary(beneficiaryId);
      if (reactivated) {
        toast('Beneficiario reactivado satisfactoriamente', {
          type: 'success'
        });
        finishContextChange();
      }
    } finally {
      setReactivatingId(null);
    }
  }

  async function searchBeneficiary() {
    setResultInfo(null);
    setShowCreate(false);
    setIsDuplicating(false);
    setReactivatingId(null);
    const matches = await searchBeneficiariesByDocument(inputValue);
    if (matches.length > 0) {
      setBeneficiaries(matches);
    } else {
      setBeneficiaries([]);
      setResultInfo('Beneficiario no existe en el sistema');
      setShowCreate(true);
    }
  }

  function handleInputChange(event) {
    const value = event.target.value;
    if (value) {
      setInputValue(value.toString().trim());
      setResultInfo(null);
      setBeneficiaries([]);
      setIsDuplicating(false);
      setReactivatingId(null);
    } else {
      setInputValue(value);
      setBeneficiaries([]);
      setResultInfo(null);
      setShowCreate(false);
      setIsDuplicating(false);
      setReactivatingId(null);
    }
  }

  async function createBeneficiary() {
    const characterizationPath = isSocialControlContext
      ? `/caracterizacion/${inputValue}/control-social/group/${info.activityGroup}`
      : isSchoolOnlyContext
        ? `/caracterizacion/${inputValue}/school/${info.school}`
        : `/caracterizacion/${inputValue}/school/${info.school}/group/${info.group}`;

    navigate(characterizationPath, {
      state: {
        backTo,
        isCopCharacterization:
          isSchoolOnlyContext || isSocialControlContext,
        controlSocialContext: isSocialControlContext
      }
    });
  }

  function getGroupName(beneficiary) {
    const code = beneficiary?.group?.code;
    if (!code) return '';
    return code.indexOf('Grupo') !== -1 ? code : `Grupo ${code}`;
  }

  function getContextInfo(beneficiary) {
    if (beneficiary?.activityGroup) {
      return (
        <div className='beneficiary-group'>
          <h2>{beneficiary.activityGroup?.activity?.name}</h2>
          <h3>Grupo {beneficiary.activityGroup?.code}</h3>
        </div>
      );
    }
    if (beneficiary?.group) {
      return (
        <div className='beneficiary-group'>
          <h2>Proceso Formativo {beneficiary.school?.code}</h2>
          <h3>{getGroupName(beneficiary)}</h3>
        </div>
      );
    }

    if (beneficiary?.school) {
      return (
        <div className='beneficiary-group'>
          <h2>Asesoria {beneficiary.school?.code}</h2>
          <h3>{beneficiary.school?.name || beneficiary.school?.address}</h3>
        </div>
      );
    }

    return null;
  }

  function getMediatorName(beneficiary) {
    const mediatorId =
      beneficiary?.user_id ??
      beneficiary?.userId ??
      beneficiary?.user?.id ??
      beneficiary?.activityGroup?.user?.id ??
      beneficiary?.school?.user?.id;
    const mediatorName = (
      beneficiary?.user?.full_name ||
      beneficiary?.activityGroup?.user?.full_name ||
      beneficiary?.school?.user?.full_name ||
      'Sin mediador'
    );

    if (mediatorId && mediatorName !== 'Sin mediador') {
      return `${mediatorName} (ID ${mediatorId})`;
    }

    return mediatorId ? `ID ${mediatorId}` : mediatorName;
  }

  function renderBeneficiaryAction(beneficiary) {
    const sameContext = isSameContext(beneficiary);
    const inactive = beneficiary?.active === false;
    const hasContext = beneficiaryHasContext(beneficiary);
    const associatedContextLabel =
      beneficiary?.group || beneficiary?.activityGroup
        ? 'un grupo'
        : 'una asesoria';

    if (sameContext && inactive) {
      return (
        <button
          type='button'
          onClick={() => reactivateInContext(beneficiary)}
          className='blue-site-button search'
          disabled={reactivatingId === getBeneficiaryId(beneficiary)}>
          {reactivatingId === getBeneficiaryId(beneficiary)
            ? 'REACTIVANDO...'
            : 'REACTIVAR'}
        </button>
      );
    }

    if (sameContext) {
      return (
        <p className='beneficiary-context-message'>
          Este beneficiario ya esta activo en este lugar.
        </p>
      );
    }

    if (!hasContext) {
      return (
        <button
          type='button'
          onClick={() => addToContext(beneficiary)}
          className='blue-site-button search'>
          {addButtonLabel}
        </button>
      );
    }

    return (
      <>
        <p className='beneficiary-context-message'>
          Este beneficiario ya esta caracterizado y asociado a {associatedContextLabel}.
        </p>
        <button
          type='button'
          onClick={() => duplicateToContext(beneficiary)}
          className='blue-site-button search'
          disabled={isDuplicating}>
          {isDuplicating ? 'DUPLICANDO...' : duplicateButtonLabel}
        </button>
      </>
    );
  }

  return (
    <>
      <section className='modal-info-container'>
        <button onClick={() => setOpenModalAdd(false)} className='button-close'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20.98'
            height='20.981'
            viewBox='0 0 20.98 20.981'>
            <g id='Icon-close' transform='translate(-308.562 -311.686)'>
              <path
                id='Trazado_679'
                data-name='Trazado 679'
                d='M-4680.317,593.807l16.738,16.738'
                transform='translate(4991 -280)'
                stroke='#bcbcbc'
                strokeLinecap='round'
                strokeWidth='3'
              />
              <path
                id='Trazado_680'
                data-name='Trazado 680'
                d='M-4663.579,593.807l-16.738,16.738'
                transform='translate(4991 -280)'
                fill='none'
                stroke='#bcbcbc'
                strokeLinecap='round'
                strokeWidth='3'
              />
            </g>
          </svg>
        </button>
        <div className='info-container'>
          <h2 className='title'>Agrega un beneficiario</h2>
          <p className='text'>
            Numero de documento <span>*</span>
          </p>
          <input
            className='input'
            type='text'
            value={inputValue}
            onChange={handleInputChange}
          />
          {beneficiaries.length === 0 && !showCreate && (
            <div className='button-container'>
              <button
                className='blue-site-button but'
                onClick={searchBeneficiary}>
                BUSCAR
              </button>
            </div>
          )}
          {beneficiaries.length > 0 && (
            <div className='beneficiary-results'>
              {beneficiaries.map(beneficiary => (
                <div className='beneficiary-info-container' key={beneficiary.id}>
                  <div className='beneficiary-name'>
                    <h3 className='text'>{beneficiary.full_name}</h3>
                  </div>
                  {getContextInfo(beneficiary)}
                  <p className='beneficiary-context-message'>
                    Mediador: {getMediatorName(beneficiary)}
                  </p>
                  {beneficiary?.active === false && (
                    <p className='beneficiary-context-message beneficiary-context-message--inactive'>
                      Registro inactivo
                    </p>
                  )}
                  {renderBeneficiaryAction(beneficiary)}
                </div>
              ))}
            </div>
          )}
          {resultInfo && (
            <div className='beneficiary-info-container'>
              <h2>{resultInfo}</h2>
            </div>
          )}
          {showCreate && (
            <button
              type={'button'}
              onClick={createBeneficiary}
              className='blue-site-button search'>
              CREAR
            </button>
          )}
        </div>
      </section>
    </>
  );
}

export default AddBeneficiaryModal;
